import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Box, CircularProgress, Slider, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import configServ from "../../../services/config";

const CrosscheckPage = forwardRef(
  (
    {
      onResponseChange,
      muscle_code,
      customer_id,
      diagnosisData,
      setDiagnosisData,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    useImperativeHandle(ref, () => ({
      handleResets,
    }));
    const [loading, setLoading] = useState(false);
    const [questionnaires, setQuestionnaires] = useState([]);
    const [responses, setResponses] = useState([]);
    const [paginatedQuestionnaires, setPaginatedQuestionnaires] = useState([]);
    const [totalPages, setTotalPages] = useState(1);

    const fetchActiveQuestionnaires = async () => {
      try {
        setLoading(true);
        const res = await configServ.getActiveQuestionnairesByItemCode({
          group_id: muscle_code,
        });
        if (res.status === 200) {
          setQuestionnaires(res.data);
          const perPage = 3;
          const total = Math.ceil(res.data.length / perPage);
          setTotalPages(total);
          setPaginatedQuestionnaires(res.data.slice(0, perPage));
        } else {
          setQuestionnaires([]);
          setPaginatedQuestionnaires([]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const handleSliderChange = (questionnaire, value) => {
      setResponses((prev) => {
        const existing = prev.find(
          (res) => res.questionnaire_id === questionnaire.id
        );
        const newResponse = {
          questionnaire_id: questionnaire.id,
          item_id: questionnaire.item_id,
          customer_id: customer_id,
          response_data: value,
        };

        const updated = existing
          ? prev.map((res) =>
              res.questionnaire_id === questionnaire.id ? newResponse : res
            )
          : [...prev, newResponse];

        // Send to parent as payload
        onResponseChange({ questionnaire_response: updated });

        return updated;
      });
    };
    const [hasMapped, setHasMapped] = useState(true);

    useEffect(() => {
      if (diagnosisData?.responses?.length > 0) {
        // console.log("i am called");
        const mappedResponses = diagnosisData.responses.map((res) => ({
          questionnaire_id: res.questionnaries_id,
          questionnaire_item_name: res.questionnaries_item_name,
          item_id: diagnosisData.item_id,
          customer_id: diagnosisData.customer_id,
          response_data: res.questionnaries_values,
        }));

        setResponses(mappedResponses);
        onResponseChange({ questionnaire_response: mappedResponses });
        // ✅ 5 seconds baad data null
        const timer = setTimeout(() => {
          setDiagnosisData(null);
        }, 5000);

        // Cleanup timer if component unmounts or diagnosisData change hota hai
        return () => clearTimeout(timer);
      }
    }, [diagnosisData, paginatedQuestionnaires]);

    useEffect(() => {
      if (muscle_code) {
        handleResets(); // clear first
        fetchActiveQuestionnaires(); // then fetch new
      }
    }, [muscle_code]);

    const handleResets = () => {
      setQuestionnaires([]);
      setResponses([]);
      setPaginatedQuestionnaires([]);
      onResponseChange({ questionnaire_response: [] }); // Send cleared responses to parent
    };

    return (
      <section className="questionaries">
        <div
          className="container"
          style={{ padding: "0 16px", maxWidth: "100%" }}
        >
          <Box sx={{ mt: { xs: 2, md: 4 } }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" } }}
            >
              Crosscheck Symptoms
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 3, fontSize: { xs: "0.9rem", md: "1rem" } }}
            >
              How often do you get these symptoms?
            </Typography>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  height: "20vh",
                }}
              >
                <CircularProgress />
              </Box>
            ) : paginatedQuestionnaires.length > 0 ? (
              paginatedQuestionnaires.map((questionnaire, index) => (
                <Box key={index} sx={{ mb: { xs: 3, md: 4 } }}>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 1,
                      overflow: "hidden", // prevents overflow
                      wordWrap: "break-word", // wraps long words
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ fontSize: { xs: "1rem", md: "1.1rem" } ,  wordBreak: "break-word",}}
                    >
                      {questionnaire.title}
                    </Typography>
                  </Box>
                  <Slider
                    value={
                      responses.find(
                        (res) => res.questionnaire_id === questionnaire.id
                      )?.response_data || 0
                    }
                    onChange={(_, val) =>
                      handleSliderChange(questionnaire, val)
                    }
                    step={1}
                    marks
                    min={1}
                    max={10}
                    valueLabelDisplay="auto"
                    sx={{
                      mx: { xs: 0, sm: 2 },
                      mt: 1,
                      color: "primary.main",
                      "& .MuiSlider-thumb": {
                        width: 24,
                        height: 24,
                        backgroundColor: "#fff",
                        border: "2px solid #1976d2",
                        boxShadow: "0 0 0 4px rgba(25, 118, 210, 0.2)",
                      },
                      "& .MuiSlider-track": {
                        height: 6,
                        borderRadius: 2,
                      },
                      "& .MuiSlider-rail": {
                        height: 6,
                        borderRadius: 2,
                        opacity: 0.3,
                      },
                      "& .MuiSlider-mark": {
                        backgroundColor: "#b0bec5",
                        height: 8,
                        width: 2,
                        marginTop: "-1px",
                      },
                      "& .MuiSlider-markLabel": {
                        fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      },
                      "& .MuiSlider-valueLabel": {
                        fontSize: "0.8rem",
                        backgroundColor: "#1976d2",
                      },
                    }}
                  />
                </Box>
              ))
            ) : (
              <Typography
                variant="h6"
                color="error"
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
              >
                {muscle_code === null
                  ? "Please select the pain area."
                  : "No Questionnaires."}
              </Typography>
            )}
          </Box>
        </div>
      </section>
    );
  }
);

export default CrosscheckPage;
